import { useCallback, useEffect, useRef } from 'react';

import {
    buildCycleInformationAutosavePayload,
    hasCycleInformationDraftChanged,
} from './cycleInformationAutosave';

import type { CycleInformationDraft } from './cycleInformationAutosave';
import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { useDebounce } from '@/hooks/useDebounce';
import { buildCycleEvaluationGroupUpdatePayload } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';

const AUTOSAVE_DELAY_MS = 500;

interface UseAutosaveCycleInformationParams {
    isReadOnly: boolean;
    group?: CycleEvaluationGroup;
    cycleEvaluationGroupId?: string;
    draft: CycleInformationDraft;
    periods: EvaluationPeriodOption[];
    offeredPeriodValues: readonly string[];
    isAudienceHydrated: boolean;
}

const useAutosaveCycleInformation = ({
    isReadOnly,
    group,
    cycleEvaluationGroupId,
    draft,
    periods,
    offeredPeriodValues,
    isAudienceHydrated,
}: UseAutosaveCycleInformationParams) => {
    const hasUserEditedRef = useRef(false);
    const lastWrittenDraftRef = useRef<string | null>(null);

    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();

    const debouncedDraft = useDebounce(draft, AUTOSAVE_DELAY_MS);

    const latestRef = useRef({
        isReadOnly,
        group,
        cycleEvaluationGroupId,
        periods,
        offeredPeriodValues,
        isAudienceHydrated,
        updateCycleEvaluationGroup,
    });
    latestRef.current = {
        isReadOnly,
        group,
        cycleEvaluationGroupId,
        periods,
        offeredPeriodValues,
        isAudienceHydrated,
        updateCycleEvaluationGroup,
    };

    const markDraftChanged = useCallback(() => {
        hasUserEditedRef.current = true;
    }, []);

    // Next sends the very same fields: recording them stops a still-pending timer from repeating it.
    const markDraftSaved = useCallback((savedDraft: CycleInformationDraft) => {
        lastWrittenDraftRef.current = JSON.stringify(savedDraft);
    }, []);

    useEffect(() => {
        const {
            isReadOnly: readOnly,
            group: currentGroup,
            cycleEvaluationGroupId: groupId,
            periods: currentPeriods,
            offeredPeriodValues: currentOfferedPeriodValues,
            isAudienceHydrated: audienceHydrated,
            updateCycleEvaluationGroup: update,
        } = latestRef.current;

        if (readOnly || !hasUserEditedRef.current || !currentGroup || !groupId) {
            return;
        }

        const writtenDraft = JSON.stringify(debouncedDraft);

        if (writtenDraft === lastWrittenDraftRef.current) {
            return;
        }

        const payload = buildCycleInformationAutosavePayload({
            draft: debouncedDraft,
            group: currentGroup,
            periods: currentPeriods,
            offeredPeriodValues: currentOfferedPeriodValues,
            isAudienceHydrated: audienceHydrated,
        });

        if (!hasCycleInformationDraftChanged({ group: currentGroup, payload })) {
            return;
        }

        // A refused draft write stays silent, retried by the next edit; Next reports its own failures.
        void update({
            id: groupId,
            payload: buildCycleEvaluationGroupUpdatePayload({
                group: currentGroup,
                name: payload.name,
                period: payload.period,
                audience: payload.audience,
            }),
        })
            .then(() => {
                lastWrittenDraftRef.current = writtenDraft;
            })
            .catch(() => undefined);
    }, [debouncedDraft]);

    return { markDraftChanged, markDraftSaved };
};

export { useAutosaveCycleInformation, AUTOSAVE_DELAY_MS };
